import type {
  ModelInfo,
  TaskContext,
  CalibrationHistory,
} from "./types.js";

// ── Token estimates per task type (from data/task-profiles.yaml) ─

const BASE_TOKENS: Record<string, number> = {
  scaffold: 10_000,
  "api-endpoint": 15_000,
  "react-component": 12_500,
  "complex-ui": 22_000,
  "database-schema": 8_000,
  integration: 20_000,
  "test-unit": 10_000,
  "test-e2e": 15_000,
  devops: 11_000,
  documentation: 6_500,
  refactoring: 23_000,
  architecture: 15_000,
  debugging: 18_000,
};

const COMPLEXITY_MULTIPLIERS: Record<string, number> = {
  S: 0.3,
  M: 1.0,
  L: 2.5,
  XL: 5.0,
};

// Capability requirements by task type
const TASK_CAPABILITIES: Record<string, string[]> = {
  scaffold: ["code"],
  "api-endpoint": ["code"],
  "react-component": ["code", "creative"],
  "complex-ui": ["code", "creative"],
  "database-schema": ["code"],
  integration: ["code"],
  "test-unit": ["code"],
  "test-e2e": ["code"],
  devops: ["code"],
  documentation: ["creative"],
  refactoring: ["code", "reasoning"],
  architecture: ["reasoning", "architecture"],
  debugging: ["code", "reasoning"],
};

// Complexity → minimum recommended tier
const COMPLEXITY_MIN_TIER: Record<string, string[]> = {
  S: ["economy", "mid", "frontier"],
  M: ["mid", "economy", "frontier"],
  L: ["mid", "frontier", "economy"],
  XL: ["frontier", "mid"],
};

export interface ScoreResult {
  score: number;
  reasons: string[];
}

/**
 * Score a model's fit for a given task.
 *
 * Weights:
 *   - Capability match:     40%
 *   - Cost efficiency:      30%
 *   - Historical accuracy:  20%
 *   - Tier appropriateness: 10%
 */
export function scoreModelForTask(
  model: ModelInfo,
  task: TaskContext,
  calibration?: CalibrationHistory,
): ScoreResult {
  const reasons: string[] = [];
  let capabilityScore = 0;
  let costScore = 0;
  let historyScore = 0;
  let tierScore = 0;

  // ── 1. Capability match (0-1) ──────────────────────────────

  const requiredCaps = task.taskType
    ? TASK_CAPABILITIES[task.taskType] ?? ["code"]
    : ["code"];

  const matchedCaps = requiredCaps.filter((c) =>
    model.capabilities.includes(c),
  );
  capabilityScore = requiredCaps.length > 0
    ? matchedCaps.length / requiredCaps.length
    : 0.5;

  if (capabilityScore >= 1) {
    reasons.push(`Full capability match (${matchedCaps.join(", ")})`);
  } else if (capabilityScore > 0) {
    const missing = requiredCaps.filter(
      (c) => !model.capabilities.includes(c),
    );
    reasons.push(`Partial capability match, missing: ${missing.join(", ")}`);
  } else {
    reasons.push("No matching capabilities");
  }

  // ── 2. Cost efficiency (0-1) ───────────────────────────────
  // Lower cost = higher score. Use a logarithmic scale so
  // the cheapest models don't dominate completely.

  const estimatedTokens = estimateTokensForTask(task);
  const costEstimate = estimateCost(model, estimatedTokens);

  // Local models (free) get high but not max cost score so they
  // don't dominate capability/tier weighting for complex tasks.
  if (model.inputPerMTok === 0 && model.outputPerMTok === 0) {
    costScore = 0.8;
    reasons.push("Free (local model)");
  } else {
    // Normalise: $0.001 → 1.0, $1.00 → ~0.33, $10 → ~0.0
    // Using inverse: 1 / (1 + cost * 10)
    costScore = 1 / (1 + costEstimate * 10);
  }

  // For S tasks cost matters more — boost economy advantage
  if (task.complexity === "S" && model.tier === "economy") {
    costScore = Math.min(1, costScore * 1.3);
    reasons.push("Economy model is cost-efficient for simple tasks");
  }

  // ── 3. Historical accuracy (0-1) ──────────────────────────

  if (calibration && calibration.entries.length > 0) {
    const relevant = calibration.entries.filter(
      (e) =>
        e.model === model.id &&
        (!task.taskType || e.taskType === task.taskType) &&
        (!task.complexity || e.complexity === task.complexity),
    );

    if (relevant.length > 0) {
      // Accuracy is actual/estimated. Ideal is 1.0.
      // Score = 1 - average deviation from 1.0
      const avgAccuracy =
        relevant.reduce((sum, e) => sum + e.accuracy, 0) / relevant.length;
      const deviation = Math.abs(1 - avgAccuracy);
      historyScore = Math.max(0, 1 - deviation);
      reasons.push(
        `Historical accuracy ${(avgAccuracy * 100).toFixed(0)}% from ${relevant.length} data points`,
      );
    } else {
      // No data for this combo — neutral score
      historyScore = 0.5;
      reasons.push("No calibration data for this task/model combination");
    }
  } else {
    historyScore = 0.5;
    reasons.push("No calibration history available");
  }

  // ── 4. Tier appropriateness (0-1) ─────────────────────────

  const tierPreference = COMPLEXITY_MIN_TIER[task.complexity] ?? [
    "mid",
    "economy",
    "frontier",
  ];
  const tierIndex = tierPreference.indexOf(model.tier);
  if (tierIndex === 0) {
    tierScore = 1.0;
    reasons.push(`${model.tier} tier is ideal for ${task.complexity} complexity`);
  } else if (tierIndex === 1) {
    tierScore = 0.6;
  } else if (tierIndex === 2) {
    tierScore = 0.3;
  } else {
    tierScore = 0.1;
  }

  // ── Weighted total ─────────────────────────────────────────

  const score =
    capabilityScore * 0.4 +
    costScore * 0.3 +
    historyScore * 0.2 +
    tierScore * 0.1;

  return { score, reasons };
}

/**
 * Estimate total tokens for a task (input + output).
 */
export function estimateTokensForTask(task: TaskContext): number {
  const base = task.taskType
    ? BASE_TOKENS[task.taskType] ?? 12_000
    : 12_000;
  const multiplier = COMPLEXITY_MULTIPLIERS[task.complexity] ?? 1.0;
  return Math.round(base * multiplier);
}

/**
 * Estimate the dollar cost of running `totalTokens` through a model.
 * Assumes a 30/70 input/output split (typical for code generation).
 */
export function estimateCost(
  model: ModelInfo,
  totalTokens: number,
): number {
  const inputTokens = totalTokens * 0.3;
  const outputTokens = totalTokens * 0.7;
  const inputCost = (inputTokens / 1_000_000) * model.inputPerMTok;
  const outputCost = (outputTokens / 1_000_000) * model.outputPerMTok;
  return inputCost + outputCost;
}
